import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { usePdpLink } from "@jti/magento";
import { ConsumablesFormStepSection } from "@jti/ui/components/ConsumablesFormStepSection";
import { Form } from "@jti/ui/components/Form";
import { MultipleFlavorsSelector } from "@jti/ui/components/MultipleFlavorsSelector";
import { SingleFlavorSelector } from "@jti/ui/components/SingleFlavorSelector";
import { useConfig } from "@jti/ui/providers/useConfig";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { SticksFormSubmitButton } from "../../components/SticksFormSubmitButton";
import { useAddProduct } from "../../hooks/useAddProduct/useAddProduct";
import { useProductData } from "../../hooks/useProductData";
import { useSticksDataContext } from "../../providers";
import { getBundleConfig, getCurrentCategorySkusFromSticksMatrix, getMixedConsumableFlavors, getSingleFlavorConsumable, handleError, updateConsumablesMatrixQuantitySearchParam, } from "../../utils";
import { getBundleProductCartPayload } from "../../utils/getBundleProductCartPayload";
import { getSingleFlavorBundleProductCartPayload } from "../../utils/getSingleFlavorBundleProductCartPayload";
import { OrderFrequencyGroup } from "./components/OrderFrequencyGroup";
import { PackagingPreferenceGroup } from "./components/PackagingPreferenceGroup";
import { QuantityGroup } from "./components/QuantityGroup";
import { getInitialQuantity } from "./utils/getInitialQuantity/getInitialQuantity";
import { useSetAggregation } from "./hooks";
import { getAggregationFromLastSingleProduct, handleOtherPackagingTransition, handleSinglePacksTransition, } from "./utils";
import styles from "./SticksForm.module.scss";
const isValidFrequencyValue = (inputValue) => {
    return ["subscription", "onetime"].includes(inputValue);
};
const isValidPackagingValue = (inputValue) => {
    return ["single_packs", "cartons", "mix_n_match"].includes(inputValue);
};
export const SticksForm = ({ isSubscriptionsEnabled = false, isOutOfStockProductSelected = false, isCartLoaded, setIsSubscriptionSelected, }) => {
    const { __ } = useTranslations();
    const { data: { sticksData, sku: currentSku, customBuyCtaLabel }, setCurrentSKU, currentProduct, } = useSticksDataContext();
    const { setAggregation } = useSetAggregation();
    const [multipleFlavorsSelected, setMultipleFlavorsSelected] = useState({});
    const [lastSingleProductSelected, setLastSingleProductSelected] = useState();
    const { displayOutOfStockStickProducts } = useConfig();
    const [showValidation, setShowValidation] = useState(false);
    const currentSticksURL = usePdpLink(currentSku);
    const form = useForm({
        defaultValues: {
            orderFrequency: currentProduct?.current.orderFrequency,
            packagingPreference: currentProduct?.current.packagingPreference,
            flavors: {},
            quantity: getInitialQuantity(currentProduct?.current.packagingPreference),
        },
    });
    const { watch, setValue } = form;
    const { packagingPreference, quantity, orderFrequency } = watch();
    useEffect(() => {
        updateConsumablesMatrixQuantitySearchParam(currentSticksURL, quantity);
    }, [currentSticksURL, quantity]);
    useEffect(() => {
        if (currentProduct.current.packagingPreference !== "mix_n_match") {
            setLastSingleProductSelected(currentProduct);
        }
    }, [currentProduct.current.sku, currentProduct.current.packagingPreference]);
    const flavorsAvailableForCurrentVariant = sticksData[packagingPreference][orderFrequency];
    const onError = () => {
        setShowValidation(true);
    };
    const { isRunning: isAddProductInProgress, addProductToCart } = useAddProduct(currentSku, undefined, false, onError);
    const categoriesSKUList = useMemo(() => getCurrentCategorySkusFromSticksMatrix(packagingPreference, orderFrequency, sticksData), [packagingPreference, orderFrequency, sticksData]);
    const { elementsStockStatus, isProductDataLoading, isProductDataLoaded, siblingItemsStockStatus, finalPrices } = useProductData({
        sku: currentSku,
        currentVariantSku: currentSku,
        categoriesSKUList,
    });
    const [selectedBundleConfig] = getBundleConfig(currentProduct.current.product?.productBundleConfiguration?.items).sticks;
    const hideOutOfStockSticks = !displayOutOfStockStickProducts;
    const multipleFlavorsSticks = useMemo(() => {
        if (packagingPreference !== "mix_n_match" || !selectedBundleConfig?.allOptions)
            return [];
        const multipleFlavorsSticks = selectedBundleConfig.allOptions.map(getMixedConsumableFlavors);
        if (hideOutOfStockSticks) {
            return multipleFlavorsSticks.filter((flavor) => {
                if (!Object.hasOwn(elementsStockStatus, flavor.id))
                    return false;
                return elementsStockStatus[flavor.id] === "IN_STOCK";
            });
        }
        return multipleFlavorsSticks;
    }, [packagingPreference, selectedBundleConfig?.allOptions, elementsStockStatus, hideOutOfStockSticks]);
    const singleFlavorSticks = useMemo(() => {
        if (packagingPreference === "mix_n_match")
            return [];
        const singleFlavorSticks = flavorsAvailableForCurrentVariant.map(getSingleFlavorConsumable);
        if (hideOutOfStockSticks) {
            return singleFlavorSticks.filter((flavor) => {
                if (!Object.hasOwn(siblingItemsStockStatus, flavor.sku))
                    return false;
                return siblingItemsStockStatus[flavor.sku] === "IN_STOCK";
            });
        }
        return singleFlavorSticks;
    }, [packagingPreference, flavorsAvailableForCurrentVariant, siblingItemsStockStatus, hideOutOfStockSticks]);
    const setSelectedFlavors = (flavorsSelection) => {
        setMultipleFlavorsSelected(flavorsSelection);
        form.setValue("flavors", flavorsSelection);
    };
    const handleSubmit = (formValues) => {
        setShowValidation(false);
        const quantity = Number(formValues.quantity);
        if (formValues.packagingPreference === "mix_n_match") {
            const payload = getBundleProductCartPayload(selectedBundleConfig?.optionId, formValues.flavors);
            return addProductToCart(quantity, payload);
        }
        if (formValues.packagingPreference === "single_packs") {
            return addProductToCart(quantity);
        }
        if (selectedBundleConfig === undefined) {
            handleError("Product misconfiguration. No options available for carton bundle product.");
            return;
        }
        const payload = getSingleFlavorBundleProductCartPayload(selectedBundleConfig);
        if (!payload) {
            handleError("Something's wrong with selected bundle config");
            return;
        }
        addProductToCart(quantity, payload);
    };
    const onSelectedFlavourChange = (sku) => {
        if (!sku || sku === currentSku) {
            return;
        }
        setCurrentSKU(sku);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const handleTransitionFromSinglePacksToSubscription = () => {
        const onSuccess = () => {
            setValue("packagingPreference", "cartons");
            setValue("quantity", "1");
        };
        const aggregationSetSuccessfully = setAggregation({
            orderFrequency: "subscription",
            packagingPreference: "cartons",
            onSuccess,
        });
        if (aggregationSetSuccessfully)
            return;
        const cartonFallbackSetSuccessfully = setAggregation({
            orderFrequency: "subscription",
            packagingPreference: "cartons",
            shouldUseFallback: true,
            onSuccess,
        });
        if (cartonFallbackSetSuccessfully)
            return;
        const mix_n_matchFallbackSetSuccessfully = setAggregation({
            orderFrequency: "subscription",
            packagingPreference: "mix_n_match",
            shouldUseFallback: true,
            onSuccess,
        });
        if (mix_n_matchFallbackSetSuccessfully)
            return;
        toast.error(__("productdetails.sticks.form.error.product.unavailable"));
    };
    const handleChangeToSubscription = () => {
        setIsSubscriptionSelected(true);
        if (packagingPreference === "single_packs") {
            return handleTransitionFromSinglePacksToSubscription();
        }
        const aggregationSetSuccessfully = setAggregation({
            orderFrequency: "subscription",
            packagingPreference,
        });
        if (aggregationSetSuccessfully)
            return;
        const fallbackSetSuccessfully = setAggregation({
            orderFrequency: "subscription",
            packagingPreference,
            shouldUseFallback: true,
        });
        if (fallbackSetSuccessfully)
            return;
        toast.error(__("productdetails.sticks.form.error.product.unavailable"));
    };
    const onFrequencyChange = (e) => {
        const { value: newFrequencyValue } = e.target;
        // this should never happen, but required if we don't want to cast and keep typescript happy
        /* istanbul ignore next */
        if (!isValidFrequencyValue(newFrequencyValue))
            return;
        setSelectedFlavors({});
        if (newFrequencyValue === "subscription") {
            return handleChangeToSubscription();
        }
        setAggregation({
            orderFrequency: "onetime",
            packagingPreference,
        });
    };
    const handleTransitionFromSubscriptionToSinglePacks = (lastSingleProductSku) => {
        const onSuccess = () => {
            setValue("orderFrequency", "onetime");
            setValue("quantity", "1");
        };
        setAggregation({
            orderFrequency: "onetime",
            packagingPreference: "single_packs",
            onSuccess,
            predefinedSku: lastSingleProductSku,
        });
    };
    const onPackagingOptionChange = (e) => {
        const { value: newPackagingValue } = e.target;
        /* istanbul ignore next */
        if (!isValidPackagingValue(newPackagingValue))
            return;
        const aggregation = getAggregationFromLastSingleProduct(packagingPreference, newPackagingValue, lastSingleProductSelected);
        if (orderFrequency === "subscription" && newPackagingValue === "single_packs") {
            return handleSinglePacksTransition(aggregation, setSelectedFlavors, handleTransitionFromSubscriptionToSinglePacks);
        }
        handleOtherPackagingTransition(aggregation, orderFrequency, newPackagingValue, setAggregation);
    };
    let step = 0;
    return (_jsxs(Form, { form: form, onSubmit: handleSubmit, dataTestId: "sticks-pdp-form", children: [isSubscriptionsEnabled && (_jsx(OrderFrequencyGroup, { onChange: onFrequencyChange, step: ++step, currentProduct: currentProduct })), _jsx(PackagingPreferenceGroup, { onChange: onPackagingOptionChange, currentProduct: currentProduct, step: ++step }), _jsx(ConsumablesFormStepSection, { title: _jsxs(_Fragment, { children: [_jsx("strong", { className: styles.stepCountLabel, children: __(`sticksForm.step.order_text.${++step}`) }), " ", __("sticksForm.step.flavors_selection.title")] }), className: styles.stepSection, "data-testid": "flavor-selector-step", children: _jsx("div", { style: { width: "100%" }, children: packagingPreference === "mix_n_match" ? (_jsx(MultipleFlavorsSelector, { limit: selectedBundleConfig?.qty, items: multipleFlavorsSticks, stockStatusElements: elementsStockStatus, onChange: setSelectedFlavors, isProductDataLoading: isProductDataLoading, shouldValidate: showValidation, defaultFlavorsSelection: multipleFlavorsSelected }, currentSku)) : (_jsx(SingleFlavorSelector, { flavors: singleFlavorSticks, initialSelection: getSingleFlavorConsumable(currentProduct.current), stockStatusElements: siblingItemsStockStatus, isProductDataLoading: isProductDataLoading, onChange: onSelectedFlavourChange }, currentSku)) }) }), _jsx(QuantityGroup, { step: ++step }), _jsx(SticksFormSubmitButton, { productName: currentProduct.current.product.name, finalPrices: finalPrices, quantity: +quantity, isProductDataLoading: isProductDataLoading, isProductDataLoaded: isProductDataLoaded, isAddProductInProgress: isAddProductInProgress, isOutOfStockProductSelected: isOutOfStockProductSelected, isCartLoaded: isCartLoaded, showPricePerMonthLabel: orderFrequency === "subscription", customBuyCtaLabel: customBuyCtaLabel })] }));
};
//# sourceMappingURL=SticksForm.js.map